import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth, applyActionCode, confirmPasswordReset, verifyPasswordResetCode } from 'firebase/auth';
import './AuthAction.css';

type ViewState =
  | { kind: 'loading'; title: string; detail?: string }
  | { kind: 'error'; title: string; detail: string }
  | { kind: 'reset'; email: string; oobCode: string }
  | { kind: 'done'; title: string; detail?: string };

function getFirebaseErrorMessage(err: any): string {
  const code = typeof err?.code === 'string' ? err.code : '';
  switch (code) {
    case 'auth/expired-action-code':
      return 'This link has expired. Please request a new one from the login page.';
    case 'auth/invalid-action-code':
      return 'This link is invalid or has already been used. Please request a new one.';
    case 'auth/user-disabled':
      return 'This account is disabled. Please contact support.';
    default:
      return (typeof err?.message === 'string' && err.message) ? err.message : 'Something went wrong. Please try again.';
  }
}

const AuthActionPage: React.FC = () => {
  const navigate = useNavigate();

  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const mode = params.get('mode') || '';
  const oobCode = params.get('oobCode') || '';

  const [state, setState] = useState<ViewState>({ kind: 'loading', title: 'Preparing secure link…' });
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function run() {
      if (!mode || !oobCode) {
        setState({
          kind: 'error',
          title: 'Missing link details',
          detail: 'This link is missing required parameters. Please request a new email.',
        });
        return;
      }

      const auth = getAuth();

      if (mode === 'resetPassword') {
        setState({ kind: 'loading', title: 'Checking reset link…' });
        try {
          const email = await verifyPasswordResetCode(auth, oobCode);
          if (cancelled) return;
          setState({ kind: 'reset', email, oobCode });
        } catch (e: any) {
          if (cancelled) return;
          setState({ kind: 'error', title: 'Reset link problem', detail: getFirebaseErrorMessage(e) });
        }
        return;
      }

      if (mode === 'verifyEmail' || mode === 'recoverEmail') {
        setState({
          kind: 'loading',
          title: mode === 'verifyEmail' ? 'Verifying your email…' : 'Recovering your email…',
        });
        try {
          await applyActionCode(auth, oobCode);
          if (cancelled) return;
          setState({
            kind: 'done',
            title: mode === 'verifyEmail' ? 'Email verified' : 'Email recovered',
            detail: 'You’re all set.',
          });
        } catch (e: any) {
          if (cancelled) return;
          setState({ kind: 'error', title: 'Link problem', detail: getFirebaseErrorMessage(e) });
        }
        return;
      }

      setState({
        kind: 'error',
        title: 'Unsupported link type',
        detail: `This link type (“${mode}”) isn’t supported yet.`,
      });
    }

    void run();
    return () => { cancelled = true; };
  }, [mode, oobCode]);

  const handleReset = async () => {
    setSubmitError(null);
    const next = pw.trim();
    if (next.length < 8) {
      setSubmitError('Please use at least 8 characters.');
      return;
    }
    if (next !== pw2) {
      setSubmitError('Passwords do not match.');
      return;
    }

    setSubmitLoading(true);
    try {
      if (state.kind !== 'reset') throw new Error('Invalid reset state.');
      const auth = getAuth();
      await confirmPasswordReset(auth, state.oobCode, next);
      setState({ kind: 'done', title: 'Password updated', detail: 'You can now sign in with your new password.' });
    } catch (e: any) {
      setSubmitError(getFirebaseErrorMessage(e));
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <div className="auth-action-container">
      <div className="auth-action-card">
        <div className="auth-action-header">
          <div className="auth-action-kicker">Overture</div>
          <div className="auth-action-title">Account link</div>
        </div>

        {state.kind === 'loading' && (
          <div className="auth-action-panel">
            <div className="auth-action-panel-title">{state.title}</div>
            {state.detail && <div className="auth-action-panel-detail">{state.detail}</div>}
            <div className="auth-action-progress">
              <div className="auth-action-progress-bar" />
            </div>
          </div>
        )}

        {state.kind === 'error' && (
          <div className="auth-action-panel error">
            <div className="auth-action-panel-title">{state.title}</div>
            <div className="auth-action-panel-detail">{state.detail}</div>
            <div className="auth-action-actions">
              <button className="auth-action-primary" onClick={() => navigate('/')}>Back to login</button>
              <a className="auth-action-secondary" href="mailto:support@overture.games">Email support</a>
            </div>
          </div>
        )}

        {state.kind === 'reset' && (
          <div className="auth-action-panel">
            <div className="auth-action-panel-title">Reset password</div>
            <div className="auth-action-panel-detail">
              Setting a new password for <span className="auth-action-email">{state.email}</span>.
            </div>

            <div className="auth-action-form">
              <label className="auth-action-label">New password</label>
              <input
                className="auth-action-input"
                type="password"
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                placeholder="At least 8 characters"
                autoComplete="new-password"
              />
              <label className="auth-action-label">Confirm password</label>
              <input
                className="auth-action-input"
                type="password"
                value={pw2}
                onChange={(e) => setPw2(e.target.value)}
                placeholder="Re-enter password"
                autoComplete="new-password"
              />
              {submitError && <div className="auth-action-inline-error">{submitError}</div>}
              <div className="auth-action-actions">
                <button className="auth-action-primary" onClick={handleReset} disabled={submitLoading}>
                  {submitLoading ? 'Updating…' : 'Update password'}
                </button>
                <button className="auth-action-secondary" onClick={() => navigate('/')}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {state.kind === 'done' && (
          <div className="auth-action-panel success">
            <div className="auth-action-panel-title">{state.title}</div>
            {state.detail && <div className="auth-action-panel-detail">{state.detail}</div>}
            <div className="auth-action-actions">
              <button className="auth-action-primary" onClick={() => navigate('/')}>Go to login</button>
            </div>
          </div>
        )}

        <div className="auth-action-footer">
          If you didn’t request this email, you can safely ignore it.
        </div>
      </div>
    </div>
  );
};

export default AuthActionPage;
